import { useMemo, useState } from 'react';
import googleLogo from '../assets/google.svg';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../lib/apiConfig';
import { phLoginOtpSent, phOauthStarted, phFailure } from '../lib/posthog';
import { getLastSignIn, markSignInStarted } from '../lib/lastSignIn';
import AuthHelp, { LastUsedTag } from '../components/AuthHelp';
import '@styles/pages/login.css';

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  // How they signed in last time on this device, for the "Last used" tag.
  const [lastUsed] = useState(getLastSignIn);
  const [loading, setLoading] = useState(false);

  // field-level errors (e.g. empty email)
  const [fieldError, setFieldError] = useState<string | null>(null);

  // global auth-level errors (e.g. no account found)
  const [authError, setAuthError] = useState<string | null>(null);

  const displayAuthError = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const urlError = params.get('error');

    if (authError) return authError;

    if (urlError === 'no_account_found') {
      return 'NO_ACCOUNT_FOUND';
    }

    return null;
  }, [authError]);

  const handleEmailLogin = async () => {
    setFieldError(null);
    setAuthError(null);

    if (!email.trim()) {
      setFieldError('Please enter your email.');
      return;
    }

    setLoading(true);

    const normalizedEmail = email.trim().toLowerCase();

    const { error } = await supabase.auth.signInWithOtp({
      email: normalizedEmail,
      options: {
        shouldCreateUser: false,
      },
    });

    setLoading(false);

    if (error) {
      if (error.status === 429) {
        // 2026-08-08: login failures fired nothing at all, so a rate-limit
        // incident or a spike of "no account found" was invisible in
        // analytics and only showed up as unexplained funnel drop.
        phFailure({ step: 'login_otp_send', message: 'rate_limited', code: 429 });
        setAuthError('RATE_LIMITED');
        return;
      }

      // Before showing "no account", check if they have an unlinked subscription
      try {
        const res = await fetch(
          `${API_BASE}/stripe/check-subscription?email=${encodeURIComponent(normalizedEmail)}`
        );
        const data = await res.json();

        if (data.exists) {
          // They paid but never created an account, send them to signup
          navigate(
            `/signup?email=${encodeURIComponent(normalizedEmail)}&returning=true`
          );
          return;
        }
      } catch {
        // If check fails, fall through to normal "no account" error
      }

      phFailure({ step: 'login_otp_send', message: 'no_account_found' });
      setAuthError('NO_ACCOUNT_FOUND');
      return;
    }

    markSignInStarted('email');
    phLoginOtpSent(normalizedEmail);
    /* `intent=login` tells /verify this is a returning user, not a new
       registration. /verify is the shared OTP screen for BOTH flows and used
       to fire Meta CompleteRegistration + Reddit SignUp unconditionally, so
       Meta's signup campaigns were being optimized against a signal
       contaminated with returning logins. See Verify.tsx. */
    navigate(
      `/verify?email=${encodeURIComponent(normalizedEmail)}&intent=login`,
    );
  };

  const loginWithGoogle = async () => {
    setFieldError(null);
    setAuthError(null);

    phOauthStarted({ provider: 'google', intent: 'login' });
    markSignInStarted('google');

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) {
      phFailure({ step: 'login_oauth_google', message: error.message });
      setAuthError(error.message);
    }
  };

  return (
    <div className="auth-container">
      <header className="auth-header">
        <a
          href="https://mediloop.ai"
          className="logo"
          aria-label="Go to Mediloop homepage"
        >
          <img
            src="/images/logos/tail-logo.png"
            alt="Mediloop"
            className="logo-img"
            width={36}
            height={36}
          />
        </a>
      </header>

      <main className="auth-card auth-card--login">
        <h1>Welcome <span className="italic">back.</span></h1>
        <p className="subtitle">
          Log in and see what Agent Loop has uncovered.
        </p>

        {displayAuthError === 'RATE_LIMITED' && (
          <div className="alert-error" role="alert">
            <strong>Too many attempts</strong>
            <p>Please wait a minute before trying again.</p>
          </div>
        )}

        {displayAuthError === 'NO_ACCOUNT_FOUND' && (
          <div className="alert-error" role="alert">
            <strong>We don't have an account for that email yet</strong>
            <p>
              Creating one is free and takes a minute.
              <br />
              <Link
                to={
                  email.trim()
                    ? `/signup?email=${encodeURIComponent(email.trim().toLowerCase())}`
                    : '/signup'
                }
              >
                Create a free account
              </Link>
              .
            </p>
            {/* Most people who hit this already have an account under a
                different email or through Google. Give them a person. */}
            <AuthHelp page="no_account" email={email} label="Think you already have one?" />
          </div>
        )}

        {/* Google first — matches the signup screen; fastest one-tap
            path, with email/OTP right below for everyone else. */}
        <button className="google-btn has-last-used" onClick={loginWithGoogle}>
          {lastUsed === 'google' && <LastUsedTag />}
          <img
            src={googleLogo}
            alt="Google"
            style={{ width: 18, height: 18 }}
          />
          Continue with Google
        </button>

        <div className="divider">
          <span />
          <p>or</p>
          <span />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleEmailLogin();
          }}
        >
          <div className="has-last-used">
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            {lastUsed === 'email' && <LastUsedTag />}
          </div>

          {fieldError && <p className="error">{fieldError}</p>}

          <button className="primary" type="submit" disabled={loading}>
            {loading ? 'Sending code…' : 'Log in'}
          </button>
        </form>

        <AuthHelp page="login" email={email} />

        <p className="footer-text">
          Don&apos;t have an account?{' '}
          <Link to="/signup">Sign up free</Link>
        </p>
        <p className="terms">
          By continuing, you agree to our{' '}
          <a
            href="https://mediloop.ai/terms-of-service"
            target="_blank"
            rel="noreferrer"
          >
            Terms
          </a>{' '}
          and{' '}
          <a
            href="https://mediloop.ai/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>
      </main>
    </div>
  );
}
